import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import validator from "validator";

import { StatusCode } from "api/protocols";
import {
  PutOrderMembershipSlots,
  TOrderMembershipSlot,
} from "api/rpc/2024-04/facilityAdmin/order/membershipSlots";
import { ICustomer } from "redux/reducers/models/customer";
import { IOrder } from "redux/reducers/models/order";

import Sheet from "components/sheet/Sheet";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import SelectCustomer from "elements/register/SelectCustomer";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";

type TSlotAssignment = {
  id: number;
  customer_id?: number;
  first_name: string;
  last_name: string;
  email: string;
  send_email: boolean;
  selected_customer?: ICustomer;
};

type AttachGroupMembershipPopupProps = {
  open: boolean;
  order?: IOrder | null;
  slots: TOrderMembershipSlot[];
  cartCustomer?: ICustomer | null;
  onCancel: () => void;
  onAssigned: (slots: TOrderMembershipSlot[]) => void;
};

function slotLabel(slot: TOrderMembershipSlot): string {
  return slot.membership?.title || `Membership ${slot.membership_id}`;
}

export default function AttachGroupMembershipPopup(props: AttachGroupMembershipPopupProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [assignments, setAssignments] = useState<TSlotAssignment[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!props.open) {
      setAssignments([]);
      return;
    }

    if (props?.slots && assignments?.length === 0) {
      setAssignments(
        props.slots.map((slot, index) => {
          const useCartCustomer = index === 0 && props.cartCustomer;
          return {
            id: slot.id,
            customer_id: slot.customer_id ?? useCartCustomer?.id,
            first_name: slot.first_name ?? useCartCustomer?.first_name ?? "",
            last_name: slot.last_name ?? useCartCustomer?.last_name ?? "",
            email: slot.email ?? useCartCustomer?.email ?? "",
            send_email: Boolean(slot.send_email),
            selected_customer: slot.customer ?? (useCartCustomer || undefined),
          };
        }),
      );
    }
  }, [props.open, props.slots, props.cartCustomer]);

  function updateAssignment(slotId: number, patch: Partial<TSlotAssignment>) {
    setAssignments(prev => prev.map(assignment => (assignment.id === slotId ? { ...assignment, ...patch } : assignment)));
  }

  function handleCustomerSelect(slotId: number, customer: ICustomer) {
    updateAssignment(slotId, {
      customer_id: customer.id,
      first_name: customer.first_name,
      last_name: customer.last_name,
      email: customer.email,
      selected_customer: customer,
    });
  }

  function assignmentIsComplete(assignment: TSlotAssignment): boolean {
    if (assignment.customer_id) {
      return true;
    }

    return Boolean(
      assignment.first_name.trim() &&
        assignment.last_name.trim() &&
        assignment.email.trim() &&
        validator.isEmail(assignment.email),
    );
  }

  const allComplete = assignments.length > 0 && assignments.every(assignmentIsComplete);

  async function handleConfirm() {
    if (!props.order?.id || !allComplete) {
      dispatch(showError(t("Assign every membership before continuing")));
      return;
    }

    setSaving(true);
    const res = await PutOrderMembershipSlots(
      {
        order_id: props.order.id,
        slots: assignments.map(assignment => ({
          id: assignment.id,
          customer_id: assignment.customer_id,
          first_name: assignment.first_name,
          last_name: assignment.last_name,
          email: assignment.email,
          send_email: assignment.send_email,
        })),
      },
      true,
    );
    setSaving(false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Unable to attach memberships")));
      return;
    }

    dispatch(showSuccess(t("Memberships attached successfully")));
    props.onAssigned(res.data ?? []);
  }

  return (
    <Sheet
      open={props.open}
      size="medium"
      closable
      backDropCancel
      title={t("Who would you like to attach the membership to?")}
      onCancel={props.onCancel}
      onOk={() => handleConfirm()}
      okText={t("Confirm")}
      okDisabled={!allComplete || saving}
      cancelText={t("Later")}
    >
      {props.slots.map((slot, index) => {
        const assignment = assignments[index];
        if (!assignment) {
          return null;
        }

        return (
          <Card key={slot.id}>
            <Card.Section>
              <div className="font-semibold mb-3">{slotLabel(slot)}</div>
              {assignment.selected_customer ? (
                <div className="mb-3">
                  <div>{assignment.selected_customer.full_name}</div>
                  <div className="text-sm text-gray-500">{assignment.selected_customer.email}</div>
                </div>
              ) : null}
              <SelectCustomer
                permissionLevel="facility"
                newCustomer
                usePortal
                clearOnSelection
                label={t("Search or create a customer")}
                onCustomerChange={customer => handleCustomerSelect(slot.id, customer)}
              />
              {!assignment.customer_id ? (
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      label={t("First Name")}
                      value={assignment.first_name}
                      onChange={event => updateAssignment(slot.id, { first_name: event.target.value })}
                    />
                    <Input
                      label={t("Last Name")}
                      value={assignment.last_name}
                      onChange={event => updateAssignment(slot.id, { last_name: event.target.value })}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Input
                      label={t("Email")}
                      value={assignment.email}
                      onChange={event => updateAssignment(slot.id, { email: event.target.value })}
                    />
                  </FormLayout.Group>
                </FormLayout>
              ) : null}
              <div className="mt-3">
                <Checkbox
                  size="medium"
                  label={t("Send email confirmation")}
                  checked={assignment.send_email}
                  onChange={event => updateAssignment(slot.id, { send_email: event.target.checked })}
                />
              </div>
            </Card.Section>
          </Card>
        );
      })}
    </Sheet>
  );
}
